import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Pill } from "@/components/common";
import {
  FileSpreadsheet, ChevronDown, ChevronRight, CheckCircle2, AlertTriangle, RotateCw, CalendarClock, Clock,
} from "lucide-react";

const STATE_META = {
  overdue: { tone: "critical", label: "Overdue", icon: AlertTriangle },
  stuck: { tone: "gold", label: "In resubmission", icon: RotateCw },
  due_today: { tone: "info", label: "Due today", icon: CalendarClock },
  upcoming: { tone: "muted", label: "Upcoming", icon: Clock },
  approved: { tone: "success", label: "Approved", icon: CheckCircle2 },
};

const slug = (s) => (s || "").replace(/\s+/g, "-").toLowerCase();
const todayStr = () => new Date().toISOString().slice(0, 10);

function StateBadge({ state, testid }) {
  const m = STATE_META[state] || STATE_META.upcoming;
  const Icon = m.icon;
  return <Pill tone={m.tone} data-testid={testid}><Icon size={11} /> {m.label}</Pill>;
}

// Inline entry: log a submission date, or an approval status (+ date).
function ActualsEntry({ scopeKey, onApply, busy }) {
  const [subDate, setSubDate] = useState(todayStr());
  const [status, setStatus] = useState("A");
  const [appDate, setAppDate] = useState(todayStr());
  return (
    <div className="flex flex-wrap items-end gap-3 mt-2 p-2.5 rounded-md bg-background/60 border border-border/60">
      <div className="flex flex-col gap-1">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Log submission</span>
        <div className="flex items-center gap-1.5">
          <input type="date" value={subDate} onChange={(e) => setSubDate(e.target.value)}
            data-testid={`tracker-submit-date-${scopeKey}`}
            className="bg-input border border-border rounded-md px-2 py-1 text-xs outline-none focus:border-gold/50" />
          <button disabled={busy} onClick={() => onApply({ actual_submission: subDate })}
            data-testid={`tracker-submit-btn-${scopeKey}`}
            className="px-2.5 py-1 rounded-md text-xs border border-gold/40 text-gold hover:bg-gold/10 disabled:opacity-50">
            Submit
          </button>
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Log approval</span>
        <div className="flex items-center gap-1.5">
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid={`tracker-status-${scopeKey}`}
            className="bg-input border border-border rounded-md px-2 py-1 text-xs outline-none focus:border-gold/50">
            <option value="A">A · Approved</option>
            <option value="B">B · Approved w/ comments</option>
            <option value="C">C · Revise &amp; resubmit</option>
            <option value="D">D · Rejected</option>
          </select>
          <input type="date" value={appDate} onChange={(e) => setAppDate(e.target.value)}
            data-testid={`tracker-approve-date-${scopeKey}`}
            className="bg-input border border-border rounded-md px-2 py-1 text-xs outline-none focus:border-gold/50" />
          <button disabled={busy} onClick={() => onApply({ status, actual_approval: appDate })}
            data-testid={`tracker-approve-btn-${scopeKey}`}
            className="px-2.5 py-1 rounded-md text-xs border border-gold/40 text-gold hover:bg-gold/10 disabled:opacity-50">
            Record
          </button>
        </div>
      </div>
    </div>
  );
}

function DelayTag({ wd, label }) {
  if (wd === null || wd === undefined) return null;
  if (wd > 0) return <span className="text-[10px] text-critical">{label} +{wd}wd late</span>;
  if (wd < 0) return <span className="text-[10px] text-success">{label} {Math.abs(wd)}wd early</span>;
  return <span className="text-[10px] text-muted-foreground">{label} on time</span>;
}

function ItemRow({ item, projectId, dept, canEdit, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const apply = async (payload) => {
    setBusy(true); setErr("");
    try {
      await api.post("/tracker/actuals", { project_id: projectId, dept, item_id: item.id, ...payload });
      await onChange();
      setOpen(false);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="border-t border-border/40" data-testid={`tracker-item-${item.id}`}>
      <div className="flex flex-wrap items-center gap-2 px-3 py-1.5 text-xs">
        <span className="flex-1 min-w-[140px]">
          {item.description}
          {item.revision_cycle > 0 && (
            <span className="ml-1.5 text-[10px] text-gold">rev {item.revision_cycle}</span>
          )}
          {item.overridden && <span className="ml-1.5 text-[10px] text-muted-foreground/70">· overridden</span>}
        </span>
        <span className="font-mono-num text-muted-foreground w-[92px]">{item.submission_target || "—"}</span>
        <span className="font-mono-num text-muted-foreground w-[92px]">{item.approval_target || "—"}</span>
        <StateBadge state={item.state} testid={`tracker-state-${item.id}`} />
        {canEdit && !item.is_approved && (
          <button onClick={() => setOpen((o) => !o)} data-testid={`tracker-item-override-${item.id}`}
            className="px-2 py-0.5 rounded-md text-[11px] border border-border text-muted-foreground hover:bg-muted">
            {open ? "Close" : "Update"}
          </button>
        )}
      </div>
      <div className="px-3 pb-1 flex gap-3">
        <DelayTag wd={item.submission_delay_wd} label="Submit" />
        <DelayTag wd={item.approval_delay_wd} label="Approve" />
      </div>
      {open && canEdit && (
        <div className="px-3 pb-2">
          <ActualsEntry scopeKey={`item-${item.id}`} onApply={apply} busy={busy} />
          {err && <div className="text-[11px] text-critical mt-1">{err}</div>}
        </div>
      )}
    </div>
  );
}

function GroupBlock({ group, projectId, dept, canEdit, onChange }) {
  const [expanded, setExpanded] = useState(group.sort_rank <= 1); // auto-open groups needing attention
  const [showGroupEntry, setShowGroupEntry] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const key = slug(group.scope_group);

  const applyGroup = async (payload) => {
    setBusy(true); setErr("");
    try {
      await api.post("/tracker/actuals", { project_id: projectId, dept, scope_group: group.scope_group, ...payload });
      await onChange();
      setShowGroupEntry(false);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const c = group.counts || {};
  const groupEntryEligible = group.items.some((i) => !i.is_approved && !i.overridden);

  return (
    <div data-testid={`tracker-group-${key}`}>
      <div className="px-3 py-1.5 bg-muted/40 flex flex-wrap items-center gap-2">
        <button onClick={() => setExpanded((e) => !e)} data-testid={`tracker-group-toggle-${key}`}
          className="flex items-center gap-1 text-[12px] font-medium text-foreground/90">
          {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          {group.scope_group} · {group.approved}/{group.total}
        </button>
        <div className="flex items-center gap-1 flex-wrap">
          {c.overdue > 0 && <Pill tone="critical">{c.overdue} overdue</Pill>}
          {c.stuck > 0 && <Pill tone="gold">{c.stuck} in resubmission</Pill>}
          {c.due_today > 0 && <Pill tone="info">{c.due_today} due today</Pill>}
          {group.approved === group.total && <Pill tone="success">Complete</Pill>}
        </div>
        {canEdit && groupEntryEligible && (
          <button onClick={() => setShowGroupEntry((s) => !s)} data-testid={`tracker-group-entry-${key}`}
            className="ml-auto px-2 py-0.5 rounded-md text-[11px] border border-gold/40 text-gold hover:bg-gold/10">
            {showGroupEntry ? "Close" : "Update group"}
          </button>
        )}
      </div>
      {showGroupEntry && canEdit && (
        <div className="px-3 pb-2">
          <div className="text-[10px] text-muted-foreground mt-1">Applies to all non-approved items in this group (overridden or resubmitting items are excluded).</div>
          <ActualsEntry scopeKey={`group-${key}`} onApply={applyGroup} busy={busy} />
          {err && <div className="text-[11px] text-critical mt-1">{err}</div>}
        </div>
      )}
      {expanded && (
        <div>
          {group.items.map((it) => (
            <ItemRow key={it.id} item={it} projectId={projectId} dept={dept} canEdit={canEdit} onChange={onChange} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TrackerPanel({ groups, projectId, dept, canEdit, onChange }) {
  const total = groups.reduce((n, g) => n + g.total, 0);
  const approved = groups.reduce((n, g) => n + g.approved, 0);
  return (
    <div className="mb-2 rounded-md border border-gold/25 bg-gold/5 overflow-hidden" data-testid="tracker-panel">
      <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-gold/90 border-b border-gold/20 flex items-center gap-1.5">
        <FileSpreadsheet size={12} /> Submittal tracker · {approved}/{total} approved
        <span className="ml-auto normal-case tracking-normal text-muted-foreground/70 text-[10px]">Ordered: overdue → resubmission → due today → approved</span>
      </div>
      <div className="hidden md:flex px-3 py-1 text-[10px] uppercase tracking-wide text-muted-foreground/70 gap-2">
        <span className="flex-1">Item</span>
        <span className="w-[92px]">Submit by</span>
        <span className="w-[92px]">Approve by</span>
        <span className="w-[120px]">Status</span>
      </div>
      {groups.map((g) => (
        <GroupBlock key={g.scope_group} group={g} projectId={projectId} dept={dept} canEdit={canEdit} onChange={onChange} />
      ))}
    </div>
  );
}
